/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { setPendingPayment, clearPendingPayment } from "../../utils/pendingPayment";
import { useTranslate } from "../useTranslate";

const POLL_INTERVAL_MS = 500;
const MESSAGE_TYPE = "sb-widget-payment-result";

const safeRgb = (color, fallback) => {
  const value = String(color?.rgb ?? "");
  return /^\d{1,3},\s*\d{1,3},\s*\d{1,3}$/.test(value) ? `rgb(${value})` : fallback;
};

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" };

const escapeHtml = (value) => String(value).replace(/[&<>"]/g, (char) => HTML_ESCAPES[char]);

// Shown while payment-init is in flight. Can't use styled-components or <T> in a document.write'd
// window, so the theme colours and the translated label are interpolated. Layout pending design.
const buildLoadingHtml = (theme, label) => {
  const colors = theme?.colors || {};
  const background = safeRgb(colors.background, "#ffffff");
  const text = safeRgb(colors.text, "#0b3052");
  const border = safeRgb(colors.border, "#e7ecf7");
  const accent = safeRgb(colors.accent, "#06adef");

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payment</title>
<style>
body{font-family:system-ui,-apple-system,sans-serif;display:flex;flex-direction:column;
align-items:center;justify-content:center;gap:24px;height:100vh;margin:0;
background:${background};color:${text}}
.sb-spinner{width:32px;height:32px;border:3px solid ${border};border-top-color:${accent};
border-radius:50%;animation:sb-spin 0.8s linear infinite}
@keyframes sb-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.sb-spinner{animation:none}}
</style></head>
<body><div class="sb-spinner"></div><p style="margin:0">${escapeHtml(label)}&hellip;</p></body></html>`;
};

/**
 * Payment popup for the framed-widget case. A window.open() window is its own
 * top-level browsing context, so the processor's later top-navigation isn't blocked by an
 * ancestor iframe's sandbox.
 *
 * @param {string|null} expectedOrigin - required origin of the relay's postMessage; it broadcasts
 *   with "*" since it can't know the opener, so this side validates. null ignores all messages.
 * @param {() => void} onComplete - called once the popup reaches the relay page.
 */
const usePaymentPopup = (expectedOrigin, onComplete) => {
  const [isPending, setIsPending] = useState(false);
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Refs so open()'s identity stays stable - call sites list it in their own deps.
  const themeRef = useRef(null);
  themeRef.current = useTheme();
  const labelRef = useRef("");
  labelRef.current = useTranslate()._t("Arranging payment");

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    stopPolling();
    popupRef.current = null;
    setIsPending(false);
    // This document saw the flow end, so the boot-time resume must not fire later.
    clearPendingPayment();
  }, [stopPolling]);

  // Call synchronously in the Pay click handler - popup blockers require a live user gesture,
  // which an awaited API response outlives. `context` ({ invoiceId, system }) is remembered so
  // the return still resolves if this document reloads (see utils/pendingPayment.js).
  const open = useCallback(
    (context) => {
      const popup = window.open("", "_blank");
      if (!popup) return null;

      try {
        popup.document.write(buildLoadingHtml(themeRef.current, labelRef.current));
        popup.document.close();
      } catch (e) {
        // best-effort placeholder only - a blank window is still fine
      }

      setPendingPayment(context);

      popupRef.current = popup;
      setIsPending(true);

      stopPolling();
      pollRef.current = setInterval(() => {
        if (popupRef.current && popupRef.current.closed) {
          cleanup();
        }
      }, POLL_INTERVAL_MS);

      return popup;
    },
    [cleanup, stopPolling],
  );

  // Call once the payment-init API responds with a redirect URL.
  const navigate = useCallback((url) => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.location.href = url;
    }
  }, []);

  // Call when no popup is needed after all (inline content, or a zero-amount invoice).
  const close = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    cleanup();
  }, [cleanup]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      if (event.data?.type !== MESSAGE_TYPE) return;
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      cleanup();
      onCompleteRef.current?.();
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [expectedOrigin, cleanup]);

  useEffect(() => stopPolling, [stopPolling]);

  return { open, navigate, close, isPending };
};

export default usePaymentPopup;
